import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Download, Copy, Check, QrCode as QrIcon, Sparkles } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';
import { useToast } from '../hooks/useToast';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortCode: string;
  linkTitle: string;
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  shortCode,
  linkTitle,
}) => {
  const { showToast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState<'M' | 'H'>('H');
  const [includeBranding, setIncludeBranding] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [svgDataUrl, setSvgDataUrl] = useState<string>('');

  const shortUrl = `${window.location.origin}/${shortCode}`;

  useEffect(() => {
    if (!isOpen || !shortCode) return;

    // Generate Canvas representation
    const canvas = canvasRef.current;
    if (canvas) {
      QRCode.toCanvas(canvas, shortUrl, {
        width: 320,
        margin: 2,
        errorCorrectionLevel: errorCorrectionLevel,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      }).then(() => {
        if (includeBranding) {
          // Draw subtle center branding badge on canvas
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = 320;
            const centerSize = 64;
            const centerPos = (size - centerSize) / 2;

            // Draw white background circle for logo
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, centerSize / 2 + 4, 0, Math.PI * 2);
            ctx.fill();

            // Draw orange brand circle
            ctx.fillStyle = '#ea580c';
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, centerSize / 2, 0, Math.PI * 2);
            ctx.fill();

            // Draw 'B' letter
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('B', size / 2, size / 2 + 2);
          }
        }
      });
    }

    // Generate Vector SVG representation
    QRCode.toString(shortUrl, {
      type: 'svg',
      margin: 2,
      errorCorrectionLevel: errorCorrectionLevel,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    }).then((svgString) => {
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      setSvgDataUrl(url);
    });
  }, [isOpen, shortCode, shortUrl, errorCorrectionLevel, includeBranding]);

  const handleCopy = async () => {
    const success = await copyToClipboard(shortUrl);
    if (success) {
      setIsCopied(true);
      showToast('Copied to clipboard', shortUrl, 'success');
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleDownloadPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `brototype-qr-${shortCode}.png`;
    a.click();
    showToast('Downloaded High-Res PNG', `Saved brototype-qr-${shortCode}.png`, 'success');
  };

  const handleDownloadSVG = () => {
    if (!svgDataUrl) return;
    const a = document.createElement('a');
    a.href = svgDataUrl;
    a.download = `brototype-qr-${shortCode}.svg`;
    a.click();
    showToast('Downloaded Vector SVG', `Saved brototype-qr-${shortCode}.svg`, 'success');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Campaign QR Code"
      description={`QR code points to ${shortUrl}`}
      maxWidth="md"
    >
      <div className="flex flex-col items-center">
        {/* QR Code Canvas container */}
        <div className="p-4 bg-white rounded-2xl shadow-xl border border-slate-700/60 flex items-center justify-center">
          <canvas ref={canvasRef} className="rounded-lg max-w-full" />
        </div>

        {/* Short URL with copy */}
        <div className="w-full mt-5 p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 overflow-hidden px-2">
            <QrIcon className="w-4 h-4 text-orange-400 shrink-0" />
            <span className="text-xs font-mono font-medium text-slate-200 truncate">
              {shortUrl}
            </span>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition-colors cursor-pointer"
          >
            {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{isCopied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>

        {/* Options */}
        <div className="w-full grid grid-cols-2 gap-3 mt-4 text-xs">
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80 flex items-center justify-between">
            <span className="text-slate-300 font-medium">Brototype Center Badge</span>
            <input
              type="checkbox"
              checked={includeBranding}
              onChange={(e) => setIncludeBranding(e.target.checked)}
              className="accent-orange-500 cursor-pointer w-4 h-4"
            />
          </div>
          <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80 flex items-center justify-between">
            <span className="text-slate-300 font-medium">Error Correction</span>
            <select
              value={errorCorrectionLevel}
              onChange={(e) => setErrorCorrectionLevel(e.target.value as any)}
              className="bg-slate-900 text-slate-200 rounded px-2 py-1 text-xs focus:outline-none cursor-pointer"
            >
              <option value="H">High (30% redundancy)</option>
              <option value="M">Medium (15%)</option>
            </select>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-3 text-center">
          QR codes encode the short link so destination updates will apply automatically without reprinting campaign materials.
        </p>

        {/* Action buttons */}
        <div className="w-full grid grid-cols-2 gap-3 mt-5">
          <Button
            variant="outline"
            onClick={handleDownloadSVG}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Download SVG
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadPNG}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Download PNG
          </Button>
        </div>
      </div>
    </Modal>
  );
};
